import { useMemo } from "react";
import type { TreeNode } from "@ms/shared-components/components/GenericModalTree";
import { AdminRecord } from "../config/panelTableConfig";

export interface UsePermissionTreeDataProps {
  permissions: any[];
  currentRecord: AdminRecord | null;
}

export const usePermissionTreeData = ({ permissions, currentRecord }: UsePermissionTreeDataProps) => {

  // 将权限数据转换为树形结构
  const permissionTreeData = useMemo((): TreeNode[] => {
    console.log("🌳 权限树数据转换:", { permissions, currentRecord, permissionsLength: permissions?.length });

    if (!permissions || !Array.isArray(permissions) || permissions.length === 0) {
      console.log("⚠️ 权限数据为空，返回空树");
      return [];
    }

    const result: TreeNode[] = [];
    const adminAccount = currentRecord?.admin_account || "";

    // 创建管理员根节点
    const adminNode: TreeNode = {
      key: `admin-${currentRecord?.id}`,
      title: `${adminAccount} (共 ${permissions.length} 个权限)`,
      children: [],
    };

    // 按服务分组权限
    const groupedByService = (permissions as any[]).reduce(
      (acc, permission) => {
        const serviceName = permission.serviceName || permission.service_name || "unknown";
        console.log("🔍 处理权限:", { permission, serviceName });
        if (!acc[serviceName]) {
          acc[serviceName] = [];
        }
        acc[serviceName].push(permission);
        return acc;
      },
      {} as Record<string, any[]>
    );

    console.log("📊 按服务分组结果:", groupedByService);

    // 为每个服务创建节点
    Object.entries(groupedByService).forEach(([serviceName, servicePermissions]) => {
      // 第二层：只显示英文服务名 (数量)
      const serviceNode: TreeNode = {
        key: `service-${serviceName}`,
        title: `${serviceName} (${(servicePermissions as any[]).length} 个权限)`,
        children: [],
      };

      // 按模块分组权限
      const groupedByModule = (servicePermissions as any[]).reduce(
        (acc, permission) => {
          // 解析权限代码，提取模块名
          const codeParts = permission.code?.split(":") || [];
          let module: string;

          if (permission.code === "*") {
            // 全局通配符权限
            module = "global";
          } else if (codeParts.length >= 2) {
            // 标准格式：service:module:action
            module = codeParts[1];
          } else {
            // 其他格式，使用服务名作为模块
            module = serviceName;
          }

          if (!acc[module]) {
            acc[module] = [];
          }
          acc[module].push(permission);
          return acc;
        },
        {} as Record<string, any[]>
      );

      // 为每个模块创建页面节点
      Object.entries(groupedByModule).forEach(([module, modulePermissions]) => {
        // 第三层：只显示英文，格式为 "service:module (数量)"
        const moduleNode: TreeNode = {
          key: `module-${serviceName}-${module}`,
          title: `${serviceName}:${module} (${(modulePermissions as any[]).length} 个操作)`,
          children: [],
        };

        // 为每个权限创建操作节点
        (modulePermissions as any[]).forEach((permission) => {
          // 第四层：只显示英文权限代码
          const permissionNode: TreeNode = {
            key: permission.id || permission.code,
            title: permission.code || "unknown",
            isLeaf: true,
          };

          moduleNode.children?.push(permissionNode);
        });

        serviceNode.children?.push(moduleNode);
      });

      adminNode.children?.push(serviceNode);
    });

    result.push(adminNode);
    console.log("🌳 最终权限树数据:", result);
    return result;
  }, [permissions, currentRecord?.id, currentRecord?.admin_account]);

  return {
    permissionTreeData,
  };
};
